import { Howler } from "howler";

export function createAlertSoundGuard({
  howler = Howler,
  target = typeof document !== "undefined" ? document : null,
} = {}) {
  const blockedSounds = new Set();
  const failedSounds = new Set();
  const listeners = new Set();
  let armed = false;
  let snapshot = { blocked: false, loadFailed: false };

  const emit = () => {
    snapshot = {
      blocked: blockedSounds.size > 0,
      loadFailed: failedSounds.size > 0,
    };
    listeners.forEach((listener) => listener());
  };

  const onActivation = () => {
    disarm();
    setTimeout(retry, 0);
  };

  function arm() {
    if (armed || !target) return;
    armed = true;
    target.addEventListener("click", onActivation, true);
    target.addEventListener("keydown", onActivation, true);
  }

  function disarm() {
    if (!armed) return;
    armed = false;
    target.removeEventListener("click", onActivation, true);
    target.removeEventListener("keydown", onActivation, true);
  }

  function retry() {
    disarm();
    if (howler?.ctx && howler.ctx.state !== "running") {
      howler.ctx.resume().catch(() => {});
    }
    const pending = [...blockedSounds];
    blockedSounds.clear();
    emit();
    pending.forEach((sound) => sound.play());
  }

  const guard = (sound) => {
    const stop = sound.stop;
    sound.stop = function (...args) {
      if (blockedSounds.delete(sound)) emit();
      return stop.apply(this, args);
    };
    sound.on("playerror", () => {
      blockedSounds.add(sound);
      arm();
      emit();
    });
    sound.on("loaderror", () => {
      failedSounds.add(sound);
      emit();
    });
    sound.on("load", () => {
      if (failedSounds.delete(sound)) emit();
    });
    return sound;
  };

  return {
    guard,
    retry,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
  };
}

const alertSoundGuard = createAlertSoundGuard();

export const guardAlertSound = alertSoundGuard.guard;
export const retryBlockedAlertSounds = alertSoundGuard.retry;
export const subscribeAlertSound = alertSoundGuard.subscribe;
export const getAlertSoundSnapshot = alertSoundGuard.getSnapshot;
